import React, { useState, useEffect } from "react";
import { Table, message, Switch } from "antd";
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import {
  updateBlogStatus,
  fetchPendingBlogsWithAuthors,
} from "../../services/blogService";
import { fetchUserById } from "../../services/userService";
import { jwtDecode } from "jwt-decode";
import { onShowSizeChange, itemRender } from "../../components/Pagination";
import { BASE_URL_IMAGE } from "../../config/api";
import { Link } from "react-router-dom";
import { encrypt } from "../../utils/encryption";

const BlogApproval = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  const breadcrumbItems = [{ label: "Blogs" }, { label: "Approval" }];

  // ✅ Fetch logged-in user
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const decoded = jwtDecode(token);
        const user = await fetchUserById(decoded.id);
        setLoggedInUserData(user);
      } catch (error) {
        console.error("Error fetching user:", error);
        localStorage.removeItem("token");
        setLoggedInUserData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  // ✅ Fetch pending blogs based on role
  useEffect(() => {
    const fetchBlogs = async () => {
      if (!loggedInUserData) return;
      setLoading(true);
      try {
        let blogsWithAuthors = [];
        switch (loggedInUserData.user_role) {
          case "1": // Admin
            blogsWithAuthors = await fetchPendingBlogsWithAuthors({
              parameter: "Admin",
            });
            break;
          case "2": // Department Head
            blogsWithAuthors = await fetchPendingBlogsWithAuthors({
              id: loggedInUserData.department_id,
              parameter: "Department",
            });
            break;
        }
        setBlogs(blogsWithAuthors);
      } catch (error) {
        console.error("Error fetching blogs:", error);
        message.error("Failed to fetch blogs");
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [loggedInUserData]);

  console.log(blogs)

  // ✅ Handle approval toggle
  // In handleStatusChange function:
  const handleStatusChange = async (blogId) => {
    if (!loggedInUserData) return;

    try {
      let parameter;
      if (loggedInUserData.user_role === "1") {
        parameter = "Admin";
      } else if (loggedInUserData.user_role === "2") {
        parameter = "Department";
      } else {
        parameter = "Faculty";
      }

      const resp = await updateBlogStatus(blogId, { parameter });

      if (resp.toggle === "yes") {
        // Instantly update toggle in UI
        setBlogs((prevBlogs) =>
          prevBlogs.map((blog) =>
            blog.blog_id === blogId ? { ...blog, status: "approved" } : blog
          )
        );

        message.success("Blog Approved successfully");
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        message.warning("Status update did not trigger toggle");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to update status");
    }
  };

  // ✅ Table Columns
  const columns = [
    {
      title: "Sr. No",
      key: "srno",
      render: (text, record, index) => index + 1,
      width: 80,
    },
    {
      title: "Blog Title",
      dataIndex: "blog_title",
      key: "blog_title",
      render: (title, record) => (
        <div className="d-flex align-items-center">
          <img
            src={`${BASE_URL_IMAGE}/uploads/blogImages/${record.blog_image}`}
            alt={title}
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: 4,
              marginRight: 12,
            }}
          />
          <span className="fw-semibold">{title}</span>
        </div>
      ),
    },
    {
      title: "Author",
      dataIndex: "blog_author_name",
      key: "author",
      render: (author) => <span className="text-muted">{author}</span>,
    },
    {
      title: "Approval",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Switch
          checked={status === "approved"}
          onChange={() => handleStatusChange(record.blog_id)}
          size="small"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="d-flex gap-1">
          <Link
            to={`/blog-detail/${encodeURIComponent(encrypt(record.blog_id))}`}
            className="btn btn-sm btn-primary"
          >
            View
          </Link>
          <Link
            to={`/edit-blog/${encodeURIComponent(encrypt(record.blog_id))}`}
            className="btn btn-sm btn-quinary"
          >
            Edit
          </Link>
        </div>
      ),
      width: 120,
    },
  ];

  return (
    <div>
      <Helmet>
        <title>Blog Approval | CRM JNEC</title>
      </Helmet>
      <Header />
      <Sidebar activeClassName="approval-blogs" />
      <div className="page-wrapper">
        <div className="content">
          <Breadcrumb items={breadcrumbItems} />
          {loading ? (
            <LoadingSpinner message="Loading blogs for approval..." />
          ) : (
            <div className="card card-table show-entire">
              <div className="card-header">
                <h4 className="card-title">Blogs Pending Approval</h4>
                <p className="card-text">Review and approve blogs</p>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={blogs}
                    rowKey="blog_id"
                    pagination={{
                      total: blogs.length,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                      onShowSizeChange,
                      itemRender,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogApproval;
